import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMyResellerOrders } from '@/hooks/useResellerOrders';
import { formatMoney } from '@/components/reseller/OrderStatusBadge';

export default function ResellerCustomersPage() {
  const { data: orders, isLoading } = useMyResellerOrders();

  const customers = useMemo(() => {
    const map = new Map<string, { email: string; name?: string; count: number; revenue: number; currency: string; last: string }>();
    for (const o of orders ?? []) {
      const key = o.customer_email;
      const cur = map.get(key) ?? { email: key, name: o.customer_name ?? undefined, count: 0, revenue: 0, currency: o.currency, last: o.created_at };
      cur.count += 1;
      if (o.status === 'activated') cur.revenue += Number(o.amount ?? 0);
      if (new Date(o.created_at) > new Date(cur.last)) cur.last = o.created_at;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Customers</h2>
      <Card>
        <CardHeader><CardTitle>{customers.length} unique customers</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : customers.length === 0 ? (
            <p className="text-muted-foreground">Enroll a customer to see them here.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Lifetime Value</TableHead>
                  <TableHead>Last Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.email}>
                    <TableCell className="font-mono text-xs">{c.email}</TableCell>
                    <TableCell>{c.name ?? '—'}</TableCell>
                    <TableCell>{c.count}</TableCell>
                    <TableCell>{formatMoney(c.revenue, c.currency)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(c.last).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
